'use client';

import {
  ACTIVATION_EVENT,
  POSTHOG_OPTED_IN_EVENT,
  isRecentSignup,
  signupEmittedKey,
} from '@/lib/analytics/activation';
import { forgetPendingMcpAuthorization } from '@/lib/mcp/pendingAuthorization';
import {
  E2E_MOCK_USER_EMAIL,
  E2E_MOCK_USER_FULL_NAME,
  E2E_MOCK_USER_ID,
} from '@/utils/e2eMockUser';
import { createClient } from '@/utils/supabase/client';
import type { Session, SupabaseClient, User } from '@supabase/supabase-js';
import posthog from 'posthog-js';
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// E2E mock auth: Playwright's addInitScript sets these globals before any
// page script runs. Using window globals is more reliable than sessionStorage
// or NEXT_PUBLIC env vars (which may not inline with bracket notation).
declare global {
  interface Window {
    __E2E_MOCK_AUTH__?: boolean;
    __E2E_MOCK_SESSION__?: Session;
  }
}

interface SupabaseAuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

/**
 * Identify the Faro RUM session with the Supabase user.id (UUID). Anonymized
 * by design — no email, no plan_tag — per the privacy posture decided when
 * this was wired up. `window.faro` is set by GrafanaObservability *only after*
 * the user has accepted analytics consent, so the optional-chain handles the
 * "no consent yet" and "no Faro env vars configured" cases as a no-op.
 */
function syncFaroUser(session: Session | null): void {
  if (typeof window === 'undefined') return;
  const api = window.faro?.api;
  if (!api) return;
  if (session?.user?.id) {
    api.setUser({ id: session.user.id });
  } else {
    api.resetUser();
  }
}

/**
 * True only when PostHog is initialised AND opted in. Since #11843 PostHog is
 * not initialised at all until analytics consent, and until then the
 * singleton runs on posthog-js's default config (`opt_out_capturing_by_default:
 * false`), so `has_opted_in_capturing()` alone reports TRUE before any consent
 * while every identify()/capture() silently no-ops. Reading it alone would burn
 * the one-shot signup guard for an event that never shipped (#4941 miss shape).
 * PostHogProvider dispatches POSTHOG_OPTED_IN_EVENT after init + opt-in, which
 * re-runs syncPostHogUser once this turns true.
 */
function isPostHogCapturing(): boolean {
  return posthog.__loaded && posthog.has_opted_in_capturing();
}

/**
 * Fire `user_logged_in` for returning users when they explicitly sign in.
 * Only fires on Supabase `SIGNED_IN` events — not on token refresh or the
 * initial session load (`getSession`). Skips new signups (they get
 * `signup_completed` instead) and users who haven't opted into analytics.
 */
function captureLoginEvent(user: User): void {
  if (typeof window === 'undefined') return;
  if (!isPostHogCapturing()) return;
  if (isRecentSignup(user.created_at, Date.now())) return;
  posthog.capture(ACTIVATION_EVENT.userLoggedIn);
}

/**
 * Identify the PostHog person with the Supabase user.id (UUID) for the
 * activation funnel (#4910), and emit `signup_completed` for freshly-created
 * accounts. Anonymized to match syncFaroUser's posture — identify by user.id
 * only, no email/plan_tag.
 *
 * PostHog runs with `person_profiles: 'identified_only'`, so this identify()
 * is what ties otherwise-anonymous funnel events to the user; on sign-out we
 * reset() to break the association. The posthog singleton no-ops until init()
 * runs (deferred to browser-idle in PostHogProvider) — `SIGNED_IN` fires after
 * the signup/login round-trip, by which time init has run, so the new-signup
 * cohort is reliably identified. A returning user's early INITIAL_SESSION may
 * race init and simply be re-identified on their next sign-in; that does not
 * affect the signup→answer funnel.
 *
 * PostHog is opt-out-by-default until analytics consent (#4940); while opted
 * out, identify()/capture() no-op and transmit nothing. We gate the whole
 * identify + signup-emit block on `has_opted_in_capturing()` so the one-shot
 * `signupEmittedKey` localStorage guard is never burned for an event that
 * never shipped — otherwise a user who consents *after* signing up would have
 * `signup_completed` permanently suppressed (the #4941 miss shape). Sign-out
 * `reset()` still runs unconditionally to clear any prior identified state.
 */
function syncPostHogUser(session: Session | null): void {
  if (typeof window === 'undefined') return;
  const user = session?.user;
  if (!user?.id) {
    posthog.reset();
    return;
  }
  if (!isPostHogCapturing()) return;
  posthog.identify(user.id);
  if (
    isRecentSignup(user.created_at, Date.now()) &&
    !localStorage.getItem(signupEmittedKey(user.id))
  ) {
    posthog.capture(ACTIVATION_EVENT.signupCompleted);
    try {
      localStorage.setItem(signupEmittedKey(user.id), '1');
    } catch {
      // localStorage unavailable (private mode / quota): the recency window
      // bounds re-emission and PostHog funnels count unique users, so a rare
      // duplicate signup event is harmless.
    }
  }
}

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(
  undefined
);

/**
 * Detect E2E mock auth mode. Checks (in priority order):
 * 1. window.__E2E_MOCK_AUTH__ — set by Playwright addInitScript (most reliable)
 * 2. sessionStorage 'e2e_mock_session' — backup mechanism
 * 3. NEXT_PUBLIC_E2E_MOCK_AUTH env var — build-time fallback
 *
 * The `NEXT_PUBLIC_E2E_MOCK_AUTH` env var is the explicit opt-in — it is never
 * set in real production deployments, only injected by the E2E CI workflow.
 */
function getE2EMockSession(): Session | null {
  if (typeof window === 'undefined') return null;

  // 1. Window global (set by addInitScript — runs before any page script)
  if (window.__E2E_MOCK_AUTH__ && window.__E2E_MOCK_SESSION__) {
    return window.__E2E_MOCK_SESSION__;
  }

  // 2. sessionStorage (backup — also set by addInitScript)
  try {
    const json = sessionStorage.getItem('e2e_mock_session');
    if (json) return JSON.parse(json) as Session;
  } catch {
    // Ignore parse errors
  }

  // 3. Env var fallback (build-time inlined by Next.js).
  if (process.env['NEXT_PUBLIC_E2E_MOCK_AUTH'] === 'true') {
    return {
      access_token: 'e2e-mock-token',
      refresh_token: 'e2e-mock-refresh',
      expires_at: Math.floor(Date.now() / 1000) + 86400,
      expires_in: 86400,
      token_type: 'bearer',
      user: {
        id: E2E_MOCK_USER_ID,
        email: E2E_MOCK_USER_EMAIL,
        role: 'authenticated',
        aud: 'authenticated',
        app_metadata: { provider: 'email' },
        user_metadata: { full_name: E2E_MOCK_USER_FULL_NAME },
        created_at: new Date().toISOString(),
      } as User,
    };
  }

  return null;
}

/**
 * Safely create the Supabase browser client.
 * Returns null if creation fails (e.g. missing env vars in E2E CI).
 */
function safeCreateClient(): SupabaseClient | null {
  try {
    return createClient();
  } catch (err) {
    // createBrowserClient throws if NEXT_PUBLIC_SUPABASE_URL or
    // NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY are missing. In E2E mock mode
    // the client is unused, so return null and let mock auth take over.
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[SupabaseAuthProvider] Failed to create Supabase client:',
        err instanceof Error ? err.message : err
      );
    }
    return null;
  }
}

// Use useLayoutEffect on client (synchronous before paint), useEffect on server
// (avoids SSR warning: "useLayoutEffect does nothing on the server").
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * SupabaseAuthProvider manages Supabase Auth session state and exposes it via context.
 * Session tokens are managed automatically via cookies by @supabase/ssr.
 *
 * E2E mock mode: When window.__E2E_MOCK_AUTH__ is set (by Playwright addInitScript),
 * or sessionStorage contains `e2e_mock_session`, or `NEXT_PUBLIC_E2E_MOCK_AUTH` is true,
 * the provider sets a fake session in useLayoutEffect (synchronous, before browser paint).
 * This ensures:
 * - No hydration mismatch (initial render matches server: isLoading=true, session=null)
 * - Mock session is set before browser paints (user never sees loading spinner)
 * - No Supabase onAuthStateChange subscription in mock mode (prevents INITIAL_SESSION
 *   with null overwriting mock session)
 * - Supabase client creation failures are caught (missing env vars in E2E CI)
 */
export function SupabaseAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Track whether we're in mock mode. Set synchronously in useLayoutEffect
  // before the normal useEffect fires, preventing the Supabase client from
  // overwriting the mock session via onAuthStateChange.
  const isMockMode = useRef(false);

  // Latest session, mirrored into a ref so the POSTHOG_OPTED_IN_EVENT listener
  // (set up once) always re-syncs against the current session rather than a
  // stale closure capture (#4966).
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  // Wrap createClient in safeCreateClient to prevent render-crashing errors
  // when env vars are missing (common in E2E CI where mock auth is used).
  const supabase = useMemo(() => safeCreateClient(), []);

  // E2E mock auth: detect and apply mock session SYNCHRONOUSLY before paint.
  // useLayoutEffect fires after DOM mutations but before the browser paints,
  // so the mock session is set before any child sees the loading state.
  // This also runs BEFORE useEffect, so the normal Supabase auth flow
  // (in the useEffect below) will see isMockMode.current === true and skip.
  useIsomorphicLayoutEffect(() => {
    const mockSession = getE2EMockSession();
    if (mockSession) {
      isMockMode.current = true;
      setSession(mockSession);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // In mock mode, session is already set by useLayoutEffect — skip
    // Supabase auth entirely to prevent INITIAL_SESSION with null
    // from overwriting the mock session.
    if (isMockMode.current || !supabase) {
      return;
    }

    // Normal auth flow
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!isMockMode.current) {
        setSession(initialSession);
        setIsLoading(false);
        syncFaroUser(initialSession);
        syncPostHogUser(initialSession);
      }
    });

    // Listen for auth state changes (login, logout, token refresh).
    // The isMockMode guard prevents late-firing SIGNED_OUT events from
    // overwriting the mock session.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!isMockMode.current) {
        setSession(newSession);
        setIsLoading(false);
        syncFaroUser(newSession);
        syncPostHogUser(newSession);
        // Track returning-user logins. SIGNED_IN is the explicit auth event;
        // INITIAL_SESSION and TOKEN_REFRESHED are passive and must not fire.
        if (event === 'SIGNED_IN' && newSession?.user) {
          captureLoginEvent(newSession.user);
        }
      }
    });

    // Consent and the auth-keyed PostHog emit live in separate providers; they
    // only meet on an auth event. When the user grants analytics consent
    // mid-session (PostHogProvider dispatches POSTHOG_OPTED_IN_EVENT), re-sync
    // against the live session so a signup_completed suppressed pre-consent
    // fires now rather than on the next sign-in / reload (#4966).
    const onOptedIn = () => syncPostHogUser(sessionRef.current);
    window.addEventListener(POSTHOG_OPTED_IN_EVENT, onOptedIn);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener(POSTHOG_OPTED_IN_EVENT, onOptedIn);
    };
  }, [supabase]);

  const signOut = async () => {
    if (supabase) {
      // Local scope: the default (global) also deletes the MCP connectors'
      // OAuth sessions, which live in the same auth.sessions table (#12037).
      await supabase.auth.signOut({ scope: 'local' });
    }
    // A pending MCP authorization belongs to the session that started it
    // (#12168): the next person to sign in on this browser must not be
    // steered onto its consent screen.
    forgetPendingMcpAuthorization();
    setSession(null);
  };

  const value: SupabaseAuthContextType = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      isLoading,
      signOut,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, isLoading]
  );

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
}

export function useSupabaseAuth() {
  const context = useContext(SupabaseAuthContext);
  if (context === undefined) {
    throw new Error(
      'useSupabaseAuth must be used within a SupabaseAuthProvider'
    );
  }
  return context;
}
